import { Counter } from '@app/components/various/Counter';
import { cn } from '@app/utils/cn';
import { findTextNodeInElement } from '@app/utils/findTextNodeInElement';
import { useResizeObserver } from '@app/utils/hooks';
import { usePopover } from '@app/utils/hooks/usePopover';
import { isDefined } from '@app/utils/is';
import React from 'react';
import { useHover } from 'react-laag';
import { CSSTransition } from 'react-transition-group';

const MIN_POPOVER_WIDTH = 320;

interface Props {
  overflowOffset?: number;
  popoverClassName?: string;
  triggerClassName?: string;
  popoverVerticalOffset?: number;
  popoverHorizontalOffset?: number;
  externalHoverProps?: ReturnType<typeof useHover>[1];
  showOverflowingCount?: boolean;
}

export const CollapsibleItemsList = ({
  children,
  popoverVerticalOffset = 0,
  popoverClassName,
  triggerClassName,
  overflowOffset = 0,
  popoverHorizontalOffset = 0,
  externalHoverProps,
  showOverflowingCount = true,
}: React.WithChildren<Props>) => {
  const [overflowingCount, setOverflowingCount] = React.useState(0);
  const [popoverWidth, setPopoverWidth] = React.useState<number | undefined>();
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);

  const wrapperChildren = Array.from(wrapperRef.current?.children ?? []) as HTMLElement[];

  const count = React.useCallback(() => {
    if (!isDefined(wrapperRef.current) || !isDefined(containerRef.current)) {
      return 0;
    }

    const parentHeight = containerRef.current.offsetHeight;

    const lastVisibleIndex = wrapperChildren.findIndex(node => {
      return node.offsetTop > parentHeight - overflowOffset;
    });

    if (lastVisibleIndex === -1) {
      return 0;
    }

    return wrapperChildren.length - lastVisibleIndex;
  }, [overflowOffset, wrapperChildren]);

  const hasOverflow = React.useMemo(() => {
    if (wrapperChildren.length === 1 && isDefined(wrapperChildren[0])) {
      const textNode = findTextNodeInElement(wrapperChildren[0]);

      return isDefined(textNode) ? textNode.scrollWidth > textNode.offsetWidth : false;
    }

    return overflowingCount > 0;
  }, [overflowingCount, wrapperChildren]);

  const getPopoverWidth = React.useCallback(() => {
    return isDefined(wrapperRef.current?.clientWidth) ? wrapperRef.current.clientWidth + 4 : undefined;
  }, []);

  React.useEffect(() => {
    setOverflowingCount(count());
    setPopoverWidth(getPopoverWidth());
  }, [count, getPopoverWidth]);

  useResizeObserver(wrapperRef, () => {
    setOverflowingCount(count());
    setPopoverWidth(getPopoverWidth());
  });

  const [isOver, hoverProps] = useHover({
    hideOnScroll: false,
  });

  const isOpen = isOver && hasOverflow;
  const isOverflowing = overflowingCount > 0;
  const isPopoverSmallerThanContainer = isDefined(popoverWidth) && popoverWidth < MIN_POPOVER_WIDTH;

  const { triggerProps, renderLayer, layerProps } = usePopover({
    escapeHandlerEnabled: false,
    layerProps: {
      containerOffset: isPopoverSmallerThanContainer ? (MIN_POPOVER_WIDTH - popoverWidth) / 2 : 10,
      isOpen,
      placement: 'bottom-center',
      possiblePlacements: ['top-center', 'bottom-center'],
      triggerOffset: popoverVerticalOffset,
    },
  });

  const popoverTransform =
    isPopoverSmallerThanContainer ? `translateX(${MIN_POPOVER_WIDTH / 2 - (containerRef?.current?.clientWidth ?? 0) / 2 - 2}px)` : 'none';

  return (
    <div className="relative h-full w-full overflow-hidden" ref={containerRef} {...externalHoverProps}>
      <div className="inline" {...triggerProps} {...hoverProps}>
        <div
          className={cn('flex h-full flex-wrap gap-y-1 overflow-hidden empty:hidden', triggerClassName, { 'pr-14': isOverflowing })}
          ref={wrapperRef}
        >
          {children}
        </div>
        {showOverflowingCount && isOverflowing && (
          <div className="absolute right-4 top-[50%] -translate-y-[50%]">
            <Counter content={`+ ${overflowingCount}`} />
          </div>
        )}
      </div>
      {renderLayer(
        <CSSTransition
          in={isOpen}
          timeout={150}
          unmountOnExit
          classNames={{
            enter: 'opacity-0',
            enterActive: '!opacity-100 transition-opacity',
            exit: 'opacity-100',
            exitActive: '!opacity-0 transition-opacity',
          }}
        >
          <div
            {...layerProps}
            {...hoverProps}
            className="relative z-popover max-h-[90vh] min-w-80"
            style={{
              width: popoverWidth,
              ...layerProps.style,
              transform: popoverTransform,
            }}
          >
            <div
              className={cn('flex flex-wrap rounded-regular bg-white shadow-2', popoverClassName)}
              style={{ transform: `translateX(${popoverHorizontalOffset}px)` }}
            >
              {children}
            </div>
          </div>
        </CSSTransition>,
      )}
    </div>
  );
};
